/**
 * Technology Actions Coordinator Hook
 *
 * Composes modal state and action handlers into a unified interface.
 * This is the main hook used by components for bulk technology operations.
 *
 * Provides:
 * - Modal state and handlers (comment, tag, remove tag)
 * - Action handlers (add comment, add/remove tags, export)
 * - Selected row count for UI feedback
 *
 * Risk Level: Low
 * - Pure composition of other hooks
 * - No side effects
 * - Clear dependencies
 */

import type { Technology } from '@/types';

import { useTechnologyActionHandlers } from './actions/useTechnologyActionHandlers';
import { useTechnologyModalState } from './useTechnologyModalState';

/**
 * Parameters for useTechnologyActions hook
 */
interface UseTechnologyActionsProps {
  /** Currently selected technologies */
  selectedTechnologies: Technology[];
  /** Number of selected rows (for toast messages and UI) */
  selectedRowsCount: number;
  /** Function to invalidate/refetch technology data */
  invalidateTechnologies: () => void;
  /** Function to set confirm status modal state */
  setConfirmStatus: (status: unknown) => void;
}

/**
 * Return type for useTechnologyActions hook
 */
interface UseTechnologyActionsReturn {
  /** Current state of all modals */
  modalState: ReturnType<typeof useTechnologyModalState>['state'];
  /** Handlers for opening and closing modals */
  modalHandlers: ReturnType<typeof useTechnologyModalState>['handlers'];
  /** Action handlers for bulk operations */
  handlers: ReturnType<typeof useTechnologyActionHandlers>;
  /** Number of selected rows */
  selectedRowsCount: number;
}

/**
 * Custom hook for coordinating technology actions
 *
 * Combines modal state management and action handlers into a single interface.
 * This is the main hook used by components that need to perform bulk operations.
 *
 * Example usage:
 * ```tsx
 * const { modalState, modalHandlers, handlers, selectedRowsCount } = useTechnologyActions({
 *   selectedTechnologies,
 *   selectedRowsCount: selectedKeys.length,
 *   invalidateTechnologies,
 *   setConfirmStatus,
 * });
 *
 * // Use in action toolbar
 * <Button onClick={modalHandlers.openCommentModal}>Add Comment</Button>
 *
 * // Use in modals
 * <CommentModal
 *   isOpen={modalState.commentModal}
 *   onClose={modalHandlers.closeCommentModal}
 *   onSubmit={handlers.handleComment}
 * />
 * ```
 *
 * @param props - Configuration object with dependencies
 * @returns Object containing modal state, handlers, and action handlers
 */
export const useTechnologyActions = ({
  selectedTechnologies,
  selectedRowsCount,
  invalidateTechnologies,
  setConfirmStatus,
}: UseTechnologyActionsProps): UseTechnologyActionsReturn => {
  // Get modal state and handlers
  const { state: modalState, handlers: modalHandlers } =
    useTechnologyModalState();

  // Get action handlers
  const actionHandlers = useTechnologyActionHandlers({
    selectedTechnologies,
    invalidateTechnologies,
    setConfirmStatus,
  });

  return {
    modalState,
    modalHandlers,
    handlers: actionHandlers,
    selectedRowsCount,
  };
};
